/**
 * ORPC client for the SPA. Hits `${VITE_SERVER_URL}/api/rpc` with
 * `credentials: "include"` so the Better-Auth cookie travels.
 *
 * Same-site cross-subdomain setup (app.* + api.* under one parent domain) +
 * Better-Auth's `crossSubDomainCookies.domain = ".gately.geerd.ma"` means
 * the cookie is `SameSite=Lax` on the parent domain — works in every modern
 * browser without `SameSite=None`.
 */
import type { AppClient } from "@gately/api";
import { createORPCClient } from "@orpc/client";
import { RPCLink } from "@orpc/client/fetch";
import { createTanstackQueryUtils } from "@orpc/tanstack-query";

import { env } from "~/env";
import { getDeviceToken } from "~/lib/station/device-token";

const link = new RPCLink({
  url: `${env.VITE_SERVER_URL}/api/rpc`,
  /**
   * The gate station's device credential, when this browser is a paired
   * station. `gateDeviceProcedure` (the account picker) runs before any
   * gardien session exists, so it authenticates on this header alone.
   *
   * Attached to every call rather than to the device-scoped ones, mirroring
   * `apps/mobile/src/utils/api.tsx`: the token only exists in storage on a
   * machine that is a station, and no procedure treats its presence as
   * anything but an extra way to be recognised. `X-Gate-Device-Token` must
   * stay on the server's CORS `allowHeaders` for this to survive preflight.
   */
  headers: () => {
    const deviceToken = getDeviceToken();
    return deviceToken ? { "X-Gate-Device-Token": deviceToken } : {};
  },
  fetch: (request, init) =>
    globalThis.fetch(request, { ...init, credentials: "include" }),
});

export const orpcClient = createORPCClient<AppClient>(link);
export const orpc = createTanstackQueryUtils(orpcClient);
